import React, { useEffect, useState } from 'react';
import {
    Switch,
    Input,
    Select,
    Modal,
    Form,
    Button,
    ColorPicker,
    Slider,
    Segmented,
    Flex,
    Divider,
    Typography,
    Upload,
    Image,
    theme as antdTheme,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ThemeLivePreview from './ThemeLivePreview';
import { useUploadThemeBackgroundMutation } from '../store/user';
import { getNotificationInstance } from '../utils/notification';
import { DARK_SURFACE_DEFAULTS } from '../utils/theme';
import type { ThemeConfig } from '../../types';
import type { Color } from 'antd/es/color-picker';
import type { UploadProps } from 'antd';

const { Title, Text } = Typography;

const ALLOWED_BG_TYPES = new Set([
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
]);

interface ThemeEditorProps {
    open: boolean;
    onSave: (name: string, theme: ThemeConfig) => void;
    onCancel: () => void;
    initialTheme: ThemeConfig | null;
    initialName: string;
}

const DEFAULT_THEME: ThemeConfig = {
    colorPrimary: '#3b82f6',
    colorInfo: '#3b82f6',
    borderRadius: 8,
    colorSuccess: '#22c55e',
    colorWarning: '#f59e0b',
    colorError: '#ef4444',
    colorLink: '#2563eb',
    colorTextBase: '#0f172a',
    colorBgBase: '#f8fafc',
    fontSize: 14,
    isCompact: false,
    isDark: false,
    fontFamily: 'IBM Plex Sans',
    backgroundImage: '',
    cardTransparency: false,
    cardWidth: 300,
    regularCardTitles: false,
};

type SliderField = 'fontSize' | 'cardWidth' | 'borderRadius';

const FONT_OPTIONS = [
    'IBM Plex Sans',
    'Roboto',
    'Arial',
    'Times New Roman',
    'Helvetica',
    'Georgia',
    'Courier New',
] as const;

/** Web fonts that need Google Fonts; the rest are system faces */
const WEB_FONTS = new Set<string>(['IBM Plex Sans', 'Roboto']);

function fontStack(fontName: string): string {
    if (fontName === 'Courier New') {
        return `'${fontName}', Courier, monospace`;
    }
    if (fontName === 'Times New Roman' || fontName === 'Georgia') {
        return `'${fontName}', Times, serif`;
    }
    return `'${fontName}', sans-serif`;
}

function ensureFontStylesheet(fontName: string) {
    if (!WEB_FONTS.has(fontName)) {
        return;
    }
    const id = `theme-font-${fontName.replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) {
        return;
    }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}&display=swap`;
    document.head.appendChild(link);
}

const ThemeEditorModal: React.FC<ThemeEditorProps> = ({
    open,
    onSave,
    onCancel,
    initialName,
    initialTheme,
}) => {
    const { token } = antdTheme.useToken();
    const [form] = Form.useForm();
    const [theme, setTheme] = useState<ThemeConfig>(initialTheme || DEFAULT_THEME);
    const [name, setName] = useState(initialName);
    // Local drafts so the thumb moves immediately; theme/preview commits on release
    const [sliderDraft, setSliderDraft] = useState<Partial<Record<SliderField, number>>>({});
    const [uploadThemeBackground, { isLoading: isUploadingBg }] = useUploadThemeBackgroundMutation();

    useEffect(() => {
        if (open) {
            setTheme(initialTheme || DEFAULT_THEME);
            setName(initialName);
            setSliderDraft({});
            FONT_OPTIONS.forEach(ensureFontStylesheet);
        }
    }, [open, initialTheme, initialName]);

    const sliderValue = (field: SliderField) =>
        sliderDraft[field] ?? theme[field];

    const fontOptions = FONT_OPTIONS.map((fontName) => ({
        value: fontName,
        label: fontName,
    }));

    const renderFontLabel = (fontName: string) => (
        <span style={{ fontFamily: fontStack(fontName) }}>{fontName}</span>
    );

    const handleColorChange = (field: string, value: Color) => {
        setTheme((prev) => ({
            ...prev,
            [field]: value.toHexString(),
        }));
    };

    const handleNumberChange = (field: string, value: number) => {
        setTheme((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSliderDraft = (field: SliderField, value: number) => {
        setSliderDraft((prev) => ({ ...prev, [field]: value }));
    };

    const handleSliderCommit = (field: SliderField, value: number) => {
        handleNumberChange(field, value);
        setSliderDraft((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleStringChange = (field: string, value: string) => {
        setTheme((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleBackgroundUpload: UploadProps['beforeUpload'] = async (file) => {
        const notification = getNotificationInstance();
        const mime = (file.type || '').toLowerCase();
        if (!ALLOWED_BG_TYPES.has(mime)) {
            notification?.error({
                message: 'Ошибка',
                description: 'Допустимы только изображения: JPG, PNG, WEBP, GIF, BMP',
            });
            return false;
        }

        const fileSizeMb = file.size / 1024 / 1024;
        if (fileSizeMb > 10) {
            notification?.error({
                message: 'Ошибка',
                description: 'Размер изображения не должен превышать 10MB',
            });
            return false;
        }

        try {
            const result = await uploadThemeBackground(file as File).unwrap();
            const imagePath = result?.message?.imagePath;
            if (!imagePath) {
                throw new Error('empty path');
            }
            handleStringChange('backgroundImage', imagePath);
        } catch {
            notification?.error({
                message: 'Ошибка',
                description: 'Не удалось загрузить фоновое изображение',
            });
        }

        return false;
    };

    const handleClearBackground = () => {
        handleStringChange('backgroundImage', '');
    };

    const handleBooleanChange = (field: string, value: boolean) => {
        setTheme((prev) => {
            const next = {
                ...prev,
                [field]: value,
            };
            // Dark UI: picker stores visible text/bg, not Ant algorithm seeds
            if (field === 'isDark' && value) {
                next.colorBgBase = '#1a1a1a';
                next.colorTextBase = '#e5e5e5';
                next.colorBgLayout = DARK_SURFACE_DEFAULTS.colorBgLayout;
                next.colorBgContainer = DARK_SURFACE_DEFAULTS.colorBgContainer;
                next.colorBgElevated = DARK_SURFACE_DEFAULTS.colorBgElevated;
                Object.assign(next, {
                    colorBorder: DARK_SURFACE_DEFAULTS.colorBorder,
                    colorBorderSecondary: DARK_SURFACE_DEFAULTS.colorBorderSecondary,
                });
            }
            if (field === 'isDark' && !value) {
                const bg = (prev.colorBgBase || '').toLowerCase();
                const text = (prev.colorTextBase || '').toLowerCase();
                const isDarkBg = bg === '#000' || bg === '#000000' || bg === '#1a1a1a' || bg === '#1f1f1f';
                const isLightText = text === '#fff' || text === '#ffffff' || text === '#e5e5e5';
                next.colorBgBase = isDarkBg ? '#f8fafc' : prev.colorBgBase;
                next.colorTextBase = isLightText ? '#0f172a' : prev.colorTextBase;
                next.colorBgLayout = undefined;
                next.colorBgContainer = undefined;
                next.colorBgElevated = undefined;
                Object.assign(next, {
                    colorBorder: undefined,
                    colorBorderSecondary: undefined,
                });
            }
            return next;
        });
    };

    const handleSave = () => {
        const committed: ThemeConfig = {
            ...theme,
            fontSize: sliderDraft.fontSize ?? theme.fontSize,
            cardWidth: sliderDraft.cardWidth ?? theme.cardWidth,
            borderRadius: sliderDraft.borderRadius ?? theme.borderRadius,
        };
        onSave(name, committed);
    };

    return (
        <Modal
            title="Редактор темы"
            open={open}
            onCancel={onCancel}
            width={920}
            footer={null}
            destroyOnHidden
        >
            <div className="theme-editor">
                <Form
                    className="theme-editor__form"
                    form={form}
                    layout="vertical"
                    initialValues={theme}
                    onFinish={handleSave}
                >
                    <Form.Item
                        label="Название"
                        rules={[
                            { required: true, message: 'Введите название темы' },
                            { max: 30, message: 'Название не должно превышать 30 символов' },
                        ]}
                    >
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Например, Ночной фокус" />
                    </Form.Item>

                    <Title level={5} style={{ marginTop: 8 }}>Режим и типографика</Title>
                    <Flex gap={16} wrap>
                        <Form.Item label="Тема" style={{ flex: 1, minWidth: 200 }}>
                            <Segmented
                                block
                                options={[
                                    { label: 'Светлая', value: 'default' },
                                    { label: 'Тёмная', value: 'dark' },
                                ]}
                                value={theme.isDark ? 'dark' : 'default'}
                                onChange={value => handleBooleanChange('isDark', value === 'dark')}
                            />
                        </Form.Item>
                        <Form.Item label="Плотность" style={{ flex: 1, minWidth: 200 }}>
                            <Segmented
                                block
                                options={[
                                    { label: 'Стандарт', value: 'default' },
                                    { label: 'Компакт', value: 'compact' },
                                ]}
                                value={theme.isCompact ? 'compact' : 'default'}
                                onChange={value => handleBooleanChange('isCompact', value === 'compact')}
                            />
                        </Form.Item>
                    </Flex>

                    <Form.Item label="Шрифт">
                        <Select
                            options={fontOptions}
                            value={theme.fontFamily}
                            onChange={(value) => handleStringChange('fontFamily', value)}
                            optionRender={(option) => renderFontLabel(String(option.value))}
                            labelRender={(props) => renderFontLabel(String(props.value))}
                        />
                    </Form.Item>
                    <Form.Item label="Размер шрифта">
                        <Slider
                            min={12}
                            max={24}
                            value={sliderValue('fontSize')}
                            onChange={(value) => handleSliderDraft('fontSize', value)}
                            onChangeComplete={(value) => handleSliderCommit('fontSize', value)}
                        />
                    </Form.Item>
                    <Form.Item label="Нежирные заголовки карточек">
                        <Switch
                            checked={Boolean(theme.regularCardTitles)}
                            onChange={(value) => handleBooleanChange('regularCardTitles', value)}
                        />
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Доска</Title>
                    <Form.Item label="Фоновое изображение">
                        <Flex gap={12} align="flex-start" wrap>
                            {theme.backgroundImage ? (
                                <div className="theme-editor__bg-preview">
                                    <Image
                                        src={theme.backgroundImage}
                                        alt="Фон темы"
                                        width={160}
                                        height={96}
                                        style={{ objectFit: 'cover', borderRadius: 8 }}
                                        preview={{ src: theme.backgroundImage }}
                                    />
                                    <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={handleClearBackground}
                                        style={{ marginTop: 4 }}
                                    >
                                        Удалить
                                    </Button>
                                </div>
                            ) : null}
                            <Upload
                                accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.bmp"
                                showUploadList={false}
                                beforeUpload={handleBackgroundUpload}
                                disabled={isUploadingBg}
                            >
                                <Button
                                    icon={<PlusOutlined />}
                                    loading={isUploadingBg}
                                >
                                    {theme.backgroundImage ? 'Заменить файл' : 'Загрузить файл'}
                                </Button>
                            </Upload>
                        </Flex>
                        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                            JPG, PNG, WEBP, GIF, BMP · до 10MB
                        </Text>
                    </Form.Item>
                    <Flex gap={16} wrap align="center">
                        <Form.Item label="Прозрачные карточки" style={{ marginBottom: 12 }}>
                            <Switch
                                checked={theme.cardTransparency}
                                onChange={(value) => handleBooleanChange('cardTransparency', value)}
                            />
                        </Form.Item>
                        <Form.Item label={`Ширина колонок: ${sliderValue('cardWidth')}px`} style={{ flex: 1, minWidth: 220 }}>
                            <Slider
                                min={220}
                                max={480}
                                value={sliderValue('cardWidth')}
                                onChange={(value) => handleSliderDraft('cardWidth', value)}
                                onChangeComplete={(value) => handleSliderCommit('cardWidth', value)}
                            />
                        </Form.Item>
                    </Flex>
                    <Form.Item label="Радиус скругления">
                        <Slider
                            min={0}
                            max={20}
                            value={sliderValue('borderRadius')}
                            onChange={(value) => handleSliderDraft('borderRadius', value)}
                            onChangeComplete={(value) => handleSliderCommit('borderRadius', value)}
                        />
                    </Form.Item>

                    <Divider />
                    <Title level={5}>Цвета</Title>

                    <div className="theme-colors">
                        <section className="theme-colors__group">
                            <Text className="theme-colors__group-title">Акценты и статусы</Text>
                            <div className="theme-colors__grid">
                                {[
                                    ['colorPrimary', 'Основной', 'Кнопки, выбор'],
                                    ['colorInfo', 'Инфо', 'Подсказки'],
                                    ['colorSuccess', 'Успех', 'Успешные действия'],
                                    ['colorWarning', 'Предупреждение', 'Предупреждения'],
                                    ['colorError', 'Ошибка', 'Ошибки, удаление'],
                                    ['colorLink', 'Ссылки', 'Текстовые ссылки'],
                                ].map(([field, label, hint]) => (
                                    <div key={field} className="theme-colors__item">
                                        <span className="theme-colors__label">{label}</span>
                                        <span className="theme-colors__hint">{hint}</span>
                                        <ColorPicker
                                            size="large"
                                            value={(theme as any)[field]}
                                            onChangeComplete={(color) => handleColorChange(field, color)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="theme-colors__group">
                            <Text className="theme-colors__group-title">Текст и фон</Text>
                            {theme.isDark && (
                                <Text type="secondary" className="theme-colors__note">
                                    В тёмной теме задают цвет надписей и подложки страницы
                                </Text>
                            )}
                            <div className="theme-colors__grid theme-colors__grid--surface">
                                {[
                                    ['colorTextBase', 'Текст', 'Надписи и иконки'],
                                    ['colorBgBase', 'Фон', 'Подложка интерфейса'],
                                ].map(([field, label, hint]) => (
                                    <div key={field} className="theme-colors__item">
                                        <span className="theme-colors__label">{label}</span>
                                        <span className="theme-colors__hint">{hint}</span>
                                        <ColorPicker
                                            size="large"
                                            value={(theme as any)[field]}
                                            onChangeComplete={(color) => handleColorChange(field, color)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Flex gap="middle" justify="end">
                            <Button onClick={onCancel}>
                                Отмена
                            </Button>
                            <Button type="primary" htmlType="submit">
                                Сохранить
                            </Button>
                        </Flex>
                    </Form.Item>
                </Form>

                <aside className="theme-editor__preview">
                    <Text className="theme-editor__preview-label" style={{ color: token.colorText }}>
                        Предпросмотр
                    </Text>
                    <ThemeLivePreview theme={theme} name={name} />
                    <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                        Предпросмотр обновляется после отпускания ползунка
                    </Text>
                </aside>
            </div>
        </Modal>
    );
};

export default ThemeEditorModal;
